import React, {useEffect, useReducer} from 'react';
import {Button, Col, Form, InputGroup, Modal, OverlayTrigger, Row, Tooltip} from '@themesberg/react-bootstrap';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faCopy, faPen, faPowerOff, faSlidersH, faTrashCan} from '@fortawesome/free-solid-svg-icons';
import {FormattedMessage} from 'react-intl';
import {useHistory} from 'react-router-dom';
import Utils from '../services/Utils';
import API from '../services/API';
import Error from '../services/Error';
import IconPicker from '../components/IconPicker';
import PageHeader from '../components/PageHeader';
import CrudTable from '../components/CrudTable';
import TranslationsField, {LanguageChip, NameWithTranslations, countTranslations} from '../components/TranslationsField';
import {usePlan} from '../services/PlanContext';


export default (props) => {

  const formReducer = (state, event) => {
    if (event.value !== undefined) {
      return {
        ...state,
        [event.name]: event.value
      }
    } else {
      return event;
    }
  }

  const {intl} = props;
  const {plan, resourceLimit, openSubscription} = usePlan();
  const history = useHistory();
  const focus = ['focus'];
  const [showModal, setShowModal] = React.useState(false);
  const [showDeleteModal, setShowDeleteModal] = React.useState(false);
  const [editModalMode, setEditModalMode] = React.useState(false);
  const [currentOptionId, setCurrentOptionId] = React.useState(false);
  const [currentOptionName, setCurrentOptionName] = React.useState('');
  const [vehicles, setVehicles] = React.useState([]);
  const [options, setOptions] = React.useState([]);
  // Limite calculée sur la liste locale (à jour après création/suppression), pas sur le compteur du plan.
  const locked = plan === 'FREE' && resourceLimit != null && options.length >= resourceLimit;
  const [formData, setFormData] = useReducer(formReducer, {});
  const [currency, setCurrency] = React.useState({code: 'EUR', symbol: '€'});
  const [icons, setIcons] = React.useState({});
  // Langue du nom principal (langue par défaut du module) : les autres langues sont saisies dans le bloc Traductions.
  const [defaultLanguage, setDefaultLanguage] = React.useState('fr');
  // Bloc Traductions déplié à l'ouverture de la modale seulement si l'option en a déjà. La modale n'est jamais
  // démontée (show bascule) : modalOpenCount sert de key au bloc pour qu'il reparte de cet état à CHAQUE ouverture,
  // sans se replier pendant la saisie.
  const [translationsOpen, setTranslationsOpen] = React.useState(false);
  const [modalOpenCount, setModalOpenCount] = React.useState(0);
  const [formatter, setFormatter] = React.useState(new Intl.NumberFormat(navigator.language, {style: 'currency', currency: 'EUR'}));

  const refreshOptions = () => {
    API.getOptions(history).then(response => {
      if (response && response.data.success === true) {
        const options = response.data.data.options.sort(function (a, b) {
          return a.position - b.position
        });
        setOptions(options);
        if (response.data.data.defaultLanguage) {
          setDefaultLanguage(response.data.data.defaultLanguage);
        }
      } else {
        Utils.notifyError(intl.formatMessage({id: 'ERROR'}), intl.formatMessage({id: 'ERROR_OCCURRED'}));
      }
    });
  }

  useEffect(() => {
    if (!Utils.isAccessToken(history)) {
      return;
    }
    refreshOptions();
    API.getCurrency(history).then(response => {
      if (response && response.data.success === true) {
        setFormatter(new Intl.NumberFormat(navigator.language, {
          style: 'currency',
          currency: response.data.data.code,
        }));
        setCurrency(response.data.data);
      }
    });
    API.getIcons(history).then(response => {
      setIcons(response.data.data.icons);
    });
  }, []);

  const getVehicles = () => {
    API.getVehicles(history).then(response => {
      if (response && response.data.success === true) {
        const vehicles = response.data.data.vehicles.sort(function (a, b) {
          return a.position - b.position
        });
        setVehicles(vehicles);
      } else {
        Utils.notifyError(intl.formatMessage({id: 'ERROR'}), intl.formatMessage({id: 'ERROR_OCCURRED'}));
      }
    });
  }

  const handleChange = event => {
    setFormData({
      name: event.target.id,
      value: event.target.value
    });
  }

  const handleChangeTranslations = (translations) => {
    setFormData({
      name: 'translations',
      value: translations
    });
  }

  const handleChangeIcon = (iconId) => {
    setFormData({
      name: 'icon',
      value: iconId
    });
  }

  const handleAddOrEdit = (e) => {
    e.preventDefault();

    if (vehicles.length === 0) {
      getVehicles();
    }

    if (editModalMode) {

      if (!formData.vehicleId) {
        formData.vehicleId = -1; // All vehicles
      }

      API.putOptions(history, formData, currentOptionId).then(response => {
        if (response && response.data.success === true) {
          Utils.notifySuccess(intl.formatMessage({id: 'OK'}), intl.formatMessage({id: 'SAVED_CHANGES'}));
          setShowModal(false);
          refreshOptions();
        } else {
          Utils.notifyError(intl.formatMessage({id: 'ERROR'}), intl.formatMessage({id: 'ERROR_OCCURRED'}));
        }
      });
    } else {

      if (!formData.vehicleId) {
        formData.vehicleId = -1; // All vehicles
      }

      API.postOptions(history, formData).then(response => {
        if (response && response.data.success === true) {
          Utils.notifySuccess(intl.formatMessage({id: 'OK'}), intl.formatMessage({id: 'OPTION_ADDED'}));
          setShowModal(false);
          refreshOptions();
        } else if (response && response.data && response.data.data === 'PLAN_LIMIT_REACHED') {
          setShowModal(false);
          openSubscription('RESOURCE_LIMIT');
        } else {
          Utils.notifyError(intl.formatMessage({id: 'ERROR'}), intl.formatMessage({id: 'ERROR_OCCURRED'}));
        }
      });
    }

  }

  const handleNewOptionButton = () => {
    if (vehicles.length === 0) {
      getVehicles();
    }

    setFormData({});
    setTranslationsOpen(false);
    setModalOpenCount(c => c + 1);
    setEditModalMode(false);
    setShowModal(true);
  }

  const handleEditOptionButton = (props) => {
    let data = Object.assign({}, props);

    if (vehicles.length === 0) {
      getVehicles();
    }

    setFormData(data);
    setTranslationsOpen(countTranslations(data.translations) > 0);
    setModalOpenCount(c => c + 1);
    setCurrentOptionId(data.id);
    setEditModalMode(true);
    setShowModal(true);
  }

  const handleCopyOptionButton = (props) => {
    API.getCopyOption(history, props.id).then(response => {
      if (response && response.data.success === true) {
        Utils.notifySuccess(intl.formatMessage({id: 'OK'}), intl.formatMessage({id: 'OPTION_COPY'}));
        refreshOptions();
      } else {
        Utils.notifyError(intl.formatMessage({id: 'ERROR'}), intl.formatMessage({id: 'ERROR_OCCURRED'}));
      }
    });
  }

  const handleEnableOrDisableButton = (props) => {
    if (props.active) {
      API.getDisableOption(history, props.id).then(response => {
        if (response && response.data.success === true) {
          Utils.notifySuccess(intl.formatMessage({id: 'OK'}), intl.formatMessage({id: 'OPTION_DISABLED'}));
          refreshOptions();
        } else {
          Utils.notifyError(intl.formatMessage({id: 'ERROR'}), intl.formatMessage({id: 'ERROR_OCCURRED'}));
        }
      });
    } else {
      API.getEnableOption(history, props.id).then(response => {
        if (response && response.data.success === true) {
          // Gratuit : activer une 2ᵉ option bascule l'active au lieu de bloquer -> toast dédié.
          const switched = response.data.data === Error.resourceSwitched();
          Utils.notifySuccess(intl.formatMessage({id: 'OK'}), intl.formatMessage({id: switched ? 'OPTION_SWITCHED' : 'OPTION_ENABLED'}));
          refreshOptions();
        } else if (response && response.data.success === false && response.data.data === Error.optionCannotBeEnabledWithoutVehicule()) {
          Utils.notifyWarning(intl.formatMessage({id: 'ERROR'}), intl.formatMessage({id: 'YOU_MUST_ASSIGN_VEHICLE_BEFORE'}));
        } else {
          Utils.notifyError(intl.formatMessage({id: 'ERROR'}), intl.formatMessage({id: 'ERROR_OCCURRED'}));
        }
      });
    }
  }

  const handleDeleteOptionButton = (props) => {
    setCurrentOptionId(props.id);
    setCurrentOptionName(props.name);
    setShowDeleteModal(true);
  }

  const deleteOption = () => {
    API.deleteOptions(history, currentOptionId).then(response => {
      if (response && response.data.success === true) {
        Utils.notifySuccess(intl.formatMessage({id: 'OK'}), intl.formatMessage({id: 'OPTION_DELETED'}));
        setShowDeleteModal(false);
        refreshOptions();
      } else {
        Utils.notifyError(intl.formatMessage({id: 'ERROR'}), intl.formatMessage({id: 'ERROR_OCCURRED'}));
      }
    });
  }

  // a little function to help us with reordering the result
  const reorder = (list, startIndex, endIndex) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);

    // update position variable
    let index = 0;
    for (const [key, value] of Object.entries(result)) {
      value.position = index++;
    }

    return result;
  };

  const handleDragEnd = (result) => {
    // dropped outside the list
    if (!result.destination) {
      return;
    }

    const items = reorder(
      options,
      result.source.index,
      result.destination.index
    );

    setOptions(items);

    // Prepare data for backend
    let data = {
      options: []
    };

    for (const [key, value] of Object.entries(items)) {
      data.options.push({
        id: value.id,
        position: value.position
      });
    }

    API.postReorderOptions(history, data).then(response => {
      if (response && response.data.success === true) {
        // Everything is OK
      } else {
        Utils.notifyError(intl.formatMessage({id: 'ERROR'}), intl.formatMessage({id: 'ERROR_OCCURRED'}));
      }
    });
  }

  const renderName = (o) => <NameWithTranslations name={o.name} translations={o.translations}/>;

  return (
    <div className="ccv2-crud">
      <PageHeader title="OPTIONS" description="OPTIONS_DESC"/>

      <CrudTable
        draggable
        getRowId={(o) => String(o.id)}
        onDragEnd={handleDragEnd}
        items={options}
        intl={intl}
        columns={[
          {key: 'icon', header: 'ICON', priority: 3, render: (o) => <div dangerouslySetInnerHTML={{__html: icons[o.icon]}}/>},
          {key: 'name', header: 'NAME', priority: 1, render: renderName},
          {key: 'vehicle', header: 'VEHICLE', priority: 2, render: (o) => (o.vehicleId === -1 ? intl.formatMessage({id: 'ALL_VEHICLES'}) : o.vehicleName)},
          {key: 'maxValue', header: 'MAX_QUANTITY', numeric: true, priority: 3, hideBelow: 'lg'},
          {key: 'pricePerQuantity', header: 'PRICE_PER_QUANTITY', numeric: true, priority: 2, render: (o) => formatter.format(o.pricePerQuantity)},
          {key: 'active', header: 'ACTIVE_TAB', type: 'status', priority: 2},
        ]}
        actions={[
          {key: 'edit', icon: faPen, label: 'EDIT', onClick: handleEditOptionButton},
          {key: 'copy', icon: faCopy, label: 'COPY', onClick: handleCopyOptionButton, hidden: () => locked},
          {key: 'toggle', icon: faPowerOff, label: (o) => (o.active ? 'DISABLE' : 'ENABLE'), intent: 'warn', onClick: handleEnableOrDisableButton},
          {key: 'delete', icon: faTrashCan, label: 'DELETE', intent: 'danger', onClick: handleDeleteOptionButton},
        ]}
        toolbar={{onNew: handleNewOptionButton, newLabel: 'NEW_OPTION', locked}}
        empty={{icon: faSlidersH, title: 'EMPTY_OPTIONS_TITLE', description: 'EMPTY_OPTIONS_DESC'}}
      />

      <Modal as={Modal.Dialog} centered show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header>
          <Modal.Title className="h6">{editModalMode ? <FormattedMessage id="EDIT_OPTION"/> : <FormattedMessage id="NEW_OPTION"/>}</Modal.Title>
          <Button variant="close" aria-label="Close" onClick={() => setShowModal(false)}/>
        </Modal.Header>
        <Modal.Body>
          <Form id="add-option-form" onSubmit={handleAddOrEdit}>
            <Row>
              <Col md={12} className="mb-3">
                <Form.Group>
                  <Form.Label className="cc-label-with-lang">
                    <FormattedMessage id="NAME"/>
                    <LanguageChip code={defaultLanguage}/>
                  </Form.Label>
                  <Form.Control required type="text" id="name" maxLength={255} placeholder={intl.formatMessage({id: 'OPTION_NAME_HINT'})}
                                onChange={handleChange} value={formData.name || ''}/>
                </Form.Group>
                <TranslationsField key={modalOpenCount} idPrefix="option-translation"
                                   value={formData.translations || {}}
                                   onChange={handleChangeTranslations}
                                   defaultLanguage={defaultLanguage}
                                   baseName={formData.name || ''}
                                   defaultOpen={translationsOpen}/>
              </Col>
            </Row>
            <Row>
              <Col md={6} className="mb-3">
                <Form.Group>
                  <Form.Label><FormattedMessage id="ICON"/></Form.Label>
                  <br/>
                  <IconPicker icons={icons} currentIcon={formData.icon} onChange={handleChangeIcon}/>
                </Form.Group>
              </Col>
              <Col md={6} className="mb-3">
                <Form.Group className="mb-3">
                  <Form.Label><FormattedMessage id="VEHICLE"/></Form.Label>
                  <Form.Select id="vehicleId" onChange={handleChange} value={formData.vehicleId}>
                    <option key={`vehicle-all`} value={-1}>{intl.formatMessage({id: 'ALL_VEHICLES'})}</option>
                    {vehicles.map(v => <option key={`vehicle-${v.id}`} value={v.id}>{v.name}</option>)}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            <br/>
            <Row>
              <Col md={6} className="mb-3">
                <Form.Group>
                  <Form.Label><FormattedMessage id="MAX_QUANTITY" values={{newLine: ' '}}/></Form.Label>
                  <OverlayTrigger trigger={focus}
                                  overlay={
                                    <Tooltip><FormattedMessage id="TOOLTIP_MAX_QUANTITY"/></Tooltip>
                                  }>
                    <Form.Control type="number" id="maxValue" min="0" max="2147483647" step="1"
                                  onChange={handleChange} value={formData.maxValue}/>
                  </OverlayTrigger>
                </Form.Group>
              </Col>
              <Col md={6} className="mb-3">
                <Form.Group>
                  <Form.Label><FormattedMessage id="PRICE_PER_QUANTITY" values={{newLine: ' '}}/></Form.Label>
                  <InputGroup>
                    <InputGroup.Text>
                      <FontAwesomeIcon icon={Utils.getCurrencyIcon(currency.code)}/>
                    </InputGroup.Text>
                    <Form.Control required type="number" id="pricePerQuantity" step="any" min="0" max="99999999.99"
                                  onChange={handleChange} value={formData.pricePerQuantity}/>
                  </InputGroup>
                </Form.Group>
              </Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="success" type="submit" form="add-option-form">
            {editModalMode ? <FormattedMessage id="RECORD"/> : <FormattedMessage id="ADD"/>}
          </Button>
          <Button variant="link" className="text-gray ms-auto" onClick={() => setShowModal(false)}>
            <FormattedMessage id="CLOSE"/>
          </Button>
        </Modal.Footer>
      </Modal>
      <Modal as={Modal.Dialog} centered show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header>
          <Modal.Title className="h6"><FormattedMessage id="DELETE_OPTION" values={{name: currentOptionName}}/></Modal.Title>
          <Button variant="close" aria-label="Close" onClick={() => setShowDeleteModal(false)}/>
        </Modal.Header>
        <Modal.Footer>
          <Button variant="danger" onClick={deleteOption}>
            <FormattedMessage id="DELETE"/>
          </Button>
          <Button variant="link" className="text-gray ms-auto" onClick={() => setShowDeleteModal(false)}>
            <FormattedMessage id="CLOSE"/>
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};
